import React, { useState } from 'react';
import { X, Calendar, Users, Mail, Phone, MessageSquare, ChevronRight, MapPin, Tag, Percent } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useCurrency } from '../contexts/CurrencyContext';
import PriceDisplay from './PriceDisplay';

interface Experience {
  id: number;
  title: string;
  location: string;
  duration: string;
  groupSize: string;
  price: number;
  originalPrice?: number; // Added for discount display
  category: string;
  image: string;
}

interface BookingModalProps {
  experience: Experience | null;
  isOpen: boolean;
  onClose: () => void;
}

const BookingModal: React.FC<BookingModalProps> = ({ experience, isOpen, onClose }) => {
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [travelers, setTravelers] = useState(2);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: ''
  });
  const { formatPrice } = useCurrency();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const formBody = {
        'form-name': 'booking-form',
        ...formData,
        date: startDate?.toLocaleDateString(),
        travelers: travelers,
        experience: experience?.title,
        location: experience?.location,
        duration: experience?.duration,
        totalPrice: experience?.price ? experience.price * travelers : 0
      };

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formBody).toString()
      });

      if (response.ok) {
        onClose();
        window.location.href = '/success';
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your booking. Please try again.');
    }
  };

  if (!isOpen || !experience) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="relative">
          <img 
            src={experience.image} 
            alt={experience.title} 
            className="w-full h-20 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
          <button 
            onClick={onClose}
            className="absolute top-2 right-2 bg-white/80 p-0.5 rounded-full hover:bg-white transition-colors"
          >
            <X className="h-3 w-3 text-gray-700" />
          </button>
          <div className="absolute bottom-2 left-2 text-white">
            <h3 className="font-bold text-xs">{experience.title}</h3>
            <div className="flex items-center">
              <MapPin className="h-2 w-2 text-ta-green mr-0.5" />
              <span className="text-white/80 text-[8px]">{experience.location}</span>
            </div>
          </div>
          {/* Discount Badge */}
          {experience.originalPrice && (
            <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-sm flex items-center">
              <Percent className="h-3 w-3 mr-0.5" />
              3% OFF
            </div>
          )}
        </div>

        <div className="p-3">
          <form 
            name="booking-form" 
            method="POST" 
            data-netlify="true"
            action="/success"
            onSubmit={handleSubmit} 
            className="space-y-3"
          >
            <input type="hidden" name="form-name" value="booking-form" />
            <input type="hidden" name="experience" value={experience.title} />
            <input type="hidden" name="location" value={experience.location} />
            <input type="hidden" name="duration" value={experience.duration} />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-ta-green focus:border-ta-green text-xs"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-ta-green focus:border-ta-green text-xs"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-ta-green focus:border-ta-green text-xs"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Phone *</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-ta-green focus:border-ta-green text-xs"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date *</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 z-10" />
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    minDate={new Date()}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-ta-green focus:border-ta-green text-xs"
                    placeholderText="Select date"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Travelers *</label>
                <div className="flex items-center">
                  <button 
                    type="button"
                    onClick={() => setTravelers(prev => Math.max(1, prev - 1))}
                    className="px-2 py-1 border border-gray-300 rounded-l-md bg-gray-50 hover:bg-gray-100 text-xs"
                  >-</button>
                  <input 
                    type="number" 
                    name="travelers"
                    value={travelers}
                    onChange={(e) => setTravelers(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-12 py-1 px-1 border-t border-b border-gray-300 text-center text-xs"
                    min="1"
                  />
                  <button 
                    type="button"
                    onClick={() => setTravelers(prev => prev + 1)}
                    className="px-2 py-1 border border-gray-300 rounded-r-md bg-gray-50 hover:bg-gray-100 text-xs"
                  >+</button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Special Requests</label>
              <textarea
                name="specialRequests"
                value={formData.specialRequests}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-ta-green focus:border-ta-green text-xs"
              />
            </div>

            <div className="bg-gray-50 p-4 rounded-md">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-600">Experience × {travelers}</span>
                <PriceDisplay price={experience.price * travelers} size="sm" />
              </div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-600">Service fee</span>
                <PriceDisplay price={Math.round(experience.price * travelers * 0.1)} size="sm" />
              </div>
              {experience.originalPrice && (
                <div className="flex justify-between text-xs mb-1 text-red-500">
                  <span>Discount (3%)</span>
                  <span>-<PriceDisplay price={Math.round((experience.originalPrice - experience.price) * travelers)} size="sm" className="text-red-500" /></span>
                </div>
              )}
              <div className="border-t border-gray-200 mt-1 pt-1 flex justify-between font-medium text-xs">
                <span>Total</span>
                <PriceDisplay price={experience.price * travelers + Math.round(experience.price * travelers * 0.1)} size="sm" />
              </div>
            </div>

            <button
              type="submit"
              className="w-full py-1.5 bg-ta-green text-white rounded-md hover:bg-ta-green-dark transition-colors text-xs flex items-center justify-center"
            >
              Complete Booking
              <ChevronRight className="ml-1 h-3 w-3" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;